// ProgrammaticPage.jsx
import React, { useEffect } from 'react';
import { Row, Col, Button, Table } from 'reactstrap';
import { Link } from 'react-router-dom';
import xsimage from './paidxs.png'

const ProgrammaticPage = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
        // Scroll animation observer
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
                }
            });
        }, { threshold: 0.1 });

        document.querySelectorAll('[data-animated]').forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const programmaticStats = [
        { number: '88%', label: 'of digital display ads bought programmatically' },
        { number: '3.2x', label: 'higher CTR with AI-optimized programmatic campaigns' },
        { number: '45%', label: 'reduction in wasted ad spend with automated bidding' },
        { number: '65%', label: 'increase in audience reach with cross-channel programmatic' }
    ];

    const programmaticApproaches = [
        {
            icon: 'fas fa-chess',
            title: 'Programmatic Strategy & Planning',
            desc: 'Comprehensive audience segmentation, channel mix planning, inventory strategy development, and KPI-aligned bid strategies for measurable programmatic success.'
        },
        {
            icon: 'fas fa-bullseye',
            title: 'Audience Targeting & Data Activation',
            desc: 'Advanced audience segmentation using behavioral signals, interest and intent data, custom lookalike modeling, and cross-device identity resolution for precision targeting.'
        },
        {
            icon: 'fas fa-layer-group',
            title: 'Cross-Channel & CTV Delivery',
            desc: 'Multi-channel programmatic execution across open web display, native advertising, Connected TV (CTV), mobile in-app inventory, and video environments.'
        },
        {
            icon: 'fas fa-robot',
            title: 'Real-Time Optimization & AI Bid Management',
            desc: 'Continuous campaign monitoring with automated bid optimization, dynamic creative performance tracking, frequency controls, and machine learning adjustments.'
        },
        {
            icon: 'fas fa-chart-bar',
            title: 'Performance Insights & Attribution Analytics',
            desc: 'Transparent reporting with custom dashboards, multi-touch attribution modeling, audience trend analysis, and actionable growth recommendations.'
        },
        {
            icon: 'fas fa-cogs',
            title: 'Technology Integration & Platform Management',
            desc: 'Expert management of Demand-Side Platforms (DSPs), Data Management Platforms (DMPs), ad exchanges, and supply-side integrations for optimal performance.'
        }
    ];

    const benefits = [
        ['Precision Audience Targeting', 'Reach specific audience segments based on behavior, intent, demographics, and contextual signals with unprecedented accuracy'],
        ['Real-Time Optimization Intelligence', 'Leverage AI-powered bidding and machine learning to continuously improve campaign performance and efficiency'],
        ['Scalable Multi-Channel Reach', 'Extend brand visibility across display, video, mobile, CTV, and native environments with unified campaign management'],
        ['Efficient Media Spend Allocation', 'Optimize budget delivery through automated systems that reduce waste and maximize return on advertising investment'],
        ['Cross-Device Campaign Coordination', 'Deliver unified messaging and consistent frequency across desktop, mobile, and connected television platforms']
    ];

    const targetBusinesses = [
        'Brands seeking to scale awareness and demand generation through automated, data-driven media buying',
        'Businesses requiring precision targeting of niche audience segments across multiple digital platforms',
        'Organizations looking to increase media efficiency and reduce wasted ad spend through AI optimization',
        'Companies supporting performance marketing goals with sophisticated automation and real-time adjustments',
        'Enterprises expanding into multi-channel digital ecosystems with unified campaign management',
        'Growth-focused businesses leveraging advanced audience insights and machine learning for competitive advantage'
    ];

    const programmaticQuestions = [
        'How can you reach your target audience across thousands of websites and apps with precision?',
        'What automated bidding strategies deliver the best ROI for your advertising objectives?',
        'How do you coordinate campaigns across display, video, mobile, and connected TV environments?',
        'What audience data signals provide the most valuable targeting insights for your business?',
        'How can real-time optimization improve campaign performance and reduce wasted ad spend?'
    ];

    return (
        <div className="paidmedia sv-page">
            <div className="sv-decorative-image-container">

            </div>

            <div className='xs-sv-page'>
                <img src={xsimage} />
            </div>
            {/* Page Title Section */}
            <div className="sv-page-title-section">
                <div className="container">
                    <h1>
                        <span className="sv-gradient-text">Programmatic Advertising</span> Services
                    </h1>
                    <p>AI-powered, data-driven advertising that automates precision, optimizes efficiency, and scales performance</p>
                </div>
            </div>

            {/* Main Content */}
            <div className="container-fluid sv-content-wrapper">
                <div className="sv-main-content">
                    {/* Hero Content Section */}
                    <section className="sv-hero-section" data-animated>
                        <Row className="align-items-center">
                            <Col lg={8}>
                                <p className="sv-lead">
                                    In an era where digital advertising is increasingly automated, programmatic advertising represents the next evolution in paid media — enabling brands to reach the right audience at the right time using real-time data and machine learning.
                                </p>
                                <p className="sv-description">
                                    At Reachtix, our <strong>Programmatic Advertising services</strong> help you drive efficient, data-led media investment that delivers meaningful performance across channels and devices. Programmatic uses automated systems, AI-powered bidding, and data signals to buy and optimize ad inventory in real time across the open web, private marketplaces, and connected TV.
                                </p>
                                <p className="sv-description">
                                    Instead of manual insertion orders and static buys, programmatic enables precision targeting based on behavior, demographics, intent, and contextual data — providing real-time optimization powered by machine learning, performance-focused media delivery at scale, and efficient spend with measurable outcomes.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-rocket mr-2"></i>
                                        Start Programmatic Campaign
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-chart-line mr-2"></i>
                                        Get Programmatic Consultation
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Stats Section */}
                    <section className="sv-stats-section">
                        <h2 className="sv-section-title" data-animated>
                            The <span className="sv-text-gradient">Programmatic Advantage</span>
                        </h2>
                        <Row className="sv-stats-grid">
                            {programmaticStats.map((stat, index) => (
                                <Col lg={6} md={6} key={index} className="mb-4" data-animated>
                                    <div className="sv-stat-card">
                                        <div className="sv-stat-number">{stat.number}</div>
                                        <div className="sv-stat-label">{stat.label}</div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </section>

                    {/* What Is Programmatic Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">What Is Programmatic Advertising?</h2>
                                <p className="sv-section-description">
                                    Programmatic advertising is the automated buying and selling of digital advertising inventory in real-time through sophisticated software platforms. It uses data, algorithms, and machine learning to deliver targeted ads to specific audiences across thousands of websites, apps, and connected devices.
                                </p>
                                <p className="sv-section-description">
                                    This approach transforms digital media from manual, relationship-based buying to data-driven, automated optimization — enabling precision targeting, real-time adjustments, and measurable performance at scale across display, video, mobile, and connected TV environments.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Key Programmatic Questions We Address:</h4>
                                    {programmaticQuestions.map((question, index) => (
                                        <div key={index} className="sv-question-item">
                                            <i className="fas fa-question-circle"></i>
                                            {question}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Why Programmatic Section */}
                    <section className="sv-benefits-overview" data-animated>
                        <Row>
                            <Col xl={12}>
                                <h2 className="sv-section-title">Why Programmatic Drives Intelligent Media Buying</h2>
                                <p className="sv-section-description">
                                    Programmatic transforms digital advertising from speculative spending to data-driven investment, enabling organizations to reach specific audiences, optimize in real-time, and scale performance with unprecedented efficiency and precision.
                                </p>
                                <div className="sv-benefits-cards">
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-bullseye"></i>
                                        <h5>Precision Audience Targeting</h5>
                                        <p>Reach specific audience segments based on behavior, intent, and data signals</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-robot"></i>
                                        <h5>AI-Powered Optimization</h5>
                                        <p>Leverage machine learning for real-time bid adjustments and performance improvements</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-expand"></i>
                                        <h5>Scalable Multi-Channel Reach</h5>
                                        <p>Extend campaigns across display, video, mobile, CTV, and native environments</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-chart-line"></i>
                                        <h5>Efficient Media Investment</h5>
                                        <p>Reduce wasted ad spend through automated optimization and performance tracking</p>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Our Programmatic Approach Section */}
                    <section className="sv-approach-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Our Programmatic Capabilities at Reachtix</h2>
                                <p className="sv-section-description">
                                    We combine strategic planning, data science expertise, and creative optimization to help you unlock the full potential of programmatic media buying across all digital channels and platforms.
                                </p>

                                <div className="sv-approaches-grid">
                                    {programmaticApproaches.map((item, index) => (
                                        <div key={index} className="sv-approach-card">
                                            <div className="sv-approach-icon">
                                                <i className={item.icon}></i>
                                            </div>
                                            <div className="sv-approach-content">
                                                <h4>{item.title}</h4>
                                                <p>{item.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Benefits Section */}
                    <section className="sv-benefits-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="mt-5 sv-section-title">Benefits of Programmatic Advertising with Reachtix</h2>
                                <div className="table-responsive">
                                    <Table borderless className="sv-benefits-table">
                                        <thead>
                                            <tr>
                                                <th>Strategic Advantage</th>
                                                <th>Business Impact</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {benefits.map(([advantage, impact], index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <i className="fas fa-check-circle"></i>
                                                        <strong>{advantage}</strong>
                                                    </td>
                                                    <td>{impact}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Who Should Invest Section */}
                    <section className="sv-target-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Who Benefits Most from Programmatic Advertising?</h2>
                                <p className="sv-section-description">
                                    Programmatic delivers maximum value for organizations seeking sophisticated, data-driven media buying with automation, precision targeting, and measurable performance optimization:
                                </p>
                                <div className="sv-target-list">
                                    {targetBusinesses.map((business, index) => (
                                        <div key={index} className="sv-target-item">
                                            <i className="fas fa-chevron-right"></i>
                                            {business}
                                        </div>
                                    ))}
                                </div>
                                <p className="sv-section-description mt-4">
                                    Whether you're an enterprise brand scaling awareness or a performance-focused business optimizing customer acquisition, programmatic advertising delivers intelligent media activation that adapts to evolving audience behavior and market dynamics.
                                </p>
                            </Col>
                        </Row>
                    </section>

                    {/* Final CTA Section */}
                    <section className="sv-main-cta" data-animated>
                        <Row>
                            <Col>
                                <h2>Ready to Activate Intelligent Programmatic Media Buying?</h2>
                                <p className="sv-cta-description">
                                    Programmatic advertising has fundamentally changed how brands connect with digital audiences. If you want campaigns that learn, adapt, and scale with your business objectives, Reachtix's <strong>Programmatic Advertising services</strong> provide your strategic advantage.
                                </p>
                                <p className="sv-cta-description">
                                    At Reachtix, we treat programmatic as a strategic growth engine — not just an automated buying tool. Our specialists blend data science with media expertise to make your programmatic advertising a powerful driver of intelligent audience engagement and measurable business outcomes.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='primary'
                                    >
                                        <i className="fas fa-robot mr-2"></i>
                                        Start Programmatic Campaign
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='light'
                                    >
                                        <i className="fas fa-search mr-2"></i>
                                        Get Programmatic Audit
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default ProgrammaticPage;